import type { Document } from "@/api/models"
import type { FilthSearchActivityState } from "@/features/chat/utils/filth-search-activity"
import type { TFunction } from "i18next"

import { isOpaqueFilthCitation, isUnresolvedFilthCitation } from "@/api/client/citations"
import { getCitationType } from "@/features/chat/components/citation.utils"
import {
  findLibraryDocumentForCitation,
  getCitationSourceForLookup,
  getDocumentCitationAlias,
  resolveCitationDisplayMeta,
  resolveLibraryCitationSource,
} from "@/utils/citation-utils"

import {
  DEFAULT_PRISM_VIEW,
  PRISM_VIEWS,
  type PrismCitations,
  type PrismSource,
  type PrismView,
} from "./prism-retrieval-constants"

export function isPrismView(value: unknown): value is PrismView {
  return typeof value === "string" && (PRISM_VIEWS as readonly string[]).includes(value)
}

/**
 * The Prism view to render (#3261). Precedence: the user's own choice when it
 * is a known view; otherwise — "company", unset, or a stored value that is not
 * a known view — the company default; then "levels". An unknown company
 * default also falls back to "levels".
 */
export function resolvePrismView(userChoice: unknown, companyDefault: unknown): PrismView {
  if (isPrismView(userChoice)) return userChoice
  return isPrismView(companyDefault) ? companyDefault : DEFAULT_PRISM_VIEW
}

/** Case- and whitespace-insensitive, like the answer's own citation lookup. */
function citationMatchKey(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase()
}

/**
 * The FILTH citation keys of a turn (`getFilthCitationKeys`), ready to match
 * the answer's citations against while the answer streams (#3261).
 */
type FilthCitationIndex = { keys: ReadonlySet<string>; libraryIds: ReadonlySet<string> }

function indexFilthCitationKeys(filthCitationKeys: readonly string[]): FilthCitationIndex {
  const keys = new Set<string>()
  const libraryIds = new Set<string>()
  for (const key of filthCitationKeys) {
    const separator = key.indexOf(":")
    if (separator <= 0) continue
    keys.add(citationMatchKey(key))
    libraryIds.add(key.slice(0, separator))
  }
  return { keys, libraryIds }
}

type CitationClass = "filth" | "other" | "pending"

/** An alias never holds a path separator; a FILTH locator always does. */
const NOT_ALIAS_SHAPED = /[\\/]/

/**
 * One citation, told apart as Prism needs it (#3261).
 *
 * The delivered documents decide first, exactly as the citation preview does
 * (`resolveCitationPreview`): the document the citation matches carries a
 * FILTH locator (`isOpaqueFilthCitation`), or the citation names a FILTH-backed
 * library that no delivered document answers to (`isUnresolvedFilthCitation`).
 * The backend sets those locators only on FILTH results, so a library's name is
 * never consulted. Like the answer's citation click, only a `library` citation
 * (`library_id:source`) is a library document.
 *
 * While the answer streams there are no documents yet. The turn's FILTH
 * citation keys stand in for them, so that a citation is classified the same
 * way before and after the documents arrive:
 *
 * - a citation whose key a grounded activity reported is FILTH;
 * - any other citation into a library that reported keys is FILTH as well, as
 *   `isUnresolvedFilthCitation` will find it once the documents arrive (a
 *   mistyped alias);
 * - except one whose source is not alias-shaped: it may be a locator that only
 *   a document can name, so it stays `pending`, neither named nor counted,
 *   until the documents arrive.
 *
 * Without documents or keys, a library citation cannot be told apart and counts
 * as `other`; the caller then holds the other-sources count back.
 */
function classifyCitation(
  citation: string,
  documents: [string, Document][] | undefined,
  filthIndex: FilthCitationIndex | undefined
): CitationClass {
  if (getCitationType(citation) !== "library") return "other"
  const document = findLibraryDocumentForCitation(citation, documents)
  if (
    isOpaqueFilthCitation(citation, document) ||
    isUnresolvedFilthCitation(citation, document, documents)
  ) {
    return "filth"
  }
  if (!filthIndex) return "other"
  const source = getCitationSourceForLookup(citation)
  if (filthIndex.keys.has(citationMatchKey(source))) return "filth"
  const separator = source.indexOf(":")
  if (separator <= 0 || !filthIndex.libraryIds.has(source.slice(0, separator))) return "other"
  if (!NOT_ALIAS_SHAPED.test(source.slice(separator + 1))) return "filth"
  return documents?.length ? "other" : "pending"
}

/**
 * True when a citation names a document of a FILTH-backed library: by the
 * delivered `documents`, or, before they arrive, by the turn's FILTH citation
 * keys (see `classifyCitation`).
 */
export function isFilthCitation(
  citation: string,
  documents?: [string, Document][],
  filthCitationKeys?: readonly string[]
): boolean {
  const filthIndex = filthCitationKeys ? indexFilthCitationKeys(filthCitationKeys) : undefined
  return classifyCitation(citation, documents, filthIndex) === "filth"
}

/** Which cited FILTH document a citation stands for, to list it once. */
function getPrismSourceKey(citation: string, documents: [string, Document][] | undefined): string {
  const match = documents?.find(
    ([, candidate]) => candidate === findLibraryDocumentForCitation(citation, documents)
  )
  if (match) {
    const [libraryId, document] = match
    return citationMatchKey(
      `${libraryId}:${getDocumentCitationAlias(document) ?? document.sourcefile ?? ""}`
    )
  }
  return citationMatchKey(resolveLibraryCitationSource(citation, documents))
}

/**
 * The turn's citations as Prism shows them (#3261), stitched into the answer's
 * own citation list rather than replacing it:
 *
 * - `sources`: one entry per cited FILTH document (`isFilthCitation`), numbered
 *   exactly like the answer's superscripts (`index + 1` over the same array),
 *   so a FILTH source keeps its number even when another citation precedes it.
 * - `otherCitationIndexes`: the answer's index of each other distinct cited
 *   source (web, M365, uploaded files, non-FILTH libraries). Prism never names
 *   them; it only says how many there are.
 *
 * Only the answer's own `citations` are read. `documents` resolves a cited
 * alias to its display name and identifies FILTH documents; before they
 * arrive, `filthCitationKeys` identify them. Neither is ever enumerated, so a
 * retrieved but uncited document can never surface here.
 *
 * A FILTH document cited twice is listed once, under its first number. It is
 * keyed by the document it resolves to, else by its citation source, which is
 * the same key: so the sources named while the answer streams are the sources
 * listed once the documents arrive, even when two documents share a title.
 */
export function getPrismCitations(
  citations: readonly string[] | undefined,
  documents?: [string, Document][],
  filthCitationKeys?: readonly string[]
): PrismCitations {
  const sources: PrismSource[] = []
  const otherCitationIndexes: number[] = []
  if (!citations?.length) return { sources, otherCitationIndexes }
  const filthIndex = filthCitationKeys ? indexFilthCitationKeys(filthCitationKeys) : undefined
  const seen = new Set<string>()
  const seenOther = new Set<string>()
  citations.forEach((citation, index) => {
    if (typeof citation !== "string" || !citation.trim()) return
    const kind = classifyCitation(citation, documents, filthIndex)
    if (kind === "pending") return
    if (kind === "other") {
      const key = getCitationSourceForLookup(citation).trim().toLowerCase()
      if (!key || seenOther.has(key)) return
      seenOther.add(key)
      otherCitationIndexes.push(index)
      return
    }
    let meta: { title: string; subtitle: string | null }
    try {
      meta = resolveCitationDisplayMeta(citation, documents)
    } catch {
      // Library names come from loaded settings; without them, still name the
      // cited file itself rather than drop a source the answer cites.
      const source = getCitationSourceForLookup(citation)
      meta = { title: source.slice(source.indexOf(":") + 1), subtitle: null }
    }
    const title = meta.title.trim()
    if (!title) return
    const key = getPrismSourceKey(citation, documents)
    if (seen.has(key)) return
    seen.add(key)
    const spaceLabel = meta.subtitle?.trim()
    sources.push({ n: index + 1, title, ...(spaceLabel ? { spaceLabel } : {}) })
  })
  return { sources, otherCitationIndexes }
}

/**
 * The key a Prism turn keeps its live progress under (#3261): the chat
 * session and the message's position, which both survive the remount that
 * `message_saved` causes when it gives the message its id. Undefined without
 * a position, and Prism then keeps no memory.
 */
export function getPrismTurnKey(
  sessionId: string | null | undefined,
  index: number | undefined
): string | undefined {
  return index === undefined ? undefined : `${sessionId ?? ""}:${index}`
}

/**
 * True while an answer is written but Prism cannot yet tell which of its
 * citations are FILTH documents (#3261). The backend delivers the answer's
 * documents only after the text: `response_complete` marks the answer done
 * first, and the documents come with `message_saved`. Until then the answer
 * still holds its streaming slot, cites at least one library document, and
 * has no documents to resolve it against. Once the slot is released the turn
 * is over whatever arrived, so this can never hold a finished turn open.
 */
export function isPrismAwaitingSources({
  inStreamingSlot,
  answerCompleted,
  citations,
  documents,
}: {
  inStreamingSlot: boolean
  answerCompleted: boolean
  citations: readonly string[] | undefined
  documents: [string, Document][] | undefined
}): boolean {
  if (!inStreamingSlot || !answerCompleted || (documents?.length ?? 0) > 0) return false
  return Boolean(
    citations?.some(
      (citation) => typeof citation === "string" && getCitationType(citation) === "library"
    )
  )
}

/**
 * How many steps of a sequence the reported data backs. While FILTH is
 * `authorizing`, identity and source access are being applied, so at most
 * the first two steps can read as done; only `grounded` completes the rest.
 */
export function getPrismStepCap(state: FilthSearchActivityState, totalSteps: number): number {
  return state.phase === "grounded" ? totalSteps : Math.min(2, totalSteps)
}

/**
 * Abstract placeholders to draw beside `namedCount` named sources, so that
 * named plus abstract items never exceed the reported evidence or `max`.
 * Before grounding there is no evidence count, so a fixed `pending` number
 * of textless shapes stands in.
 */
export function getPrismPlaceholderCount(
  state: FilthSearchActivityState,
  namedCount: number,
  { max, pending }: { max: number; pending: number }
): number {
  if (state.phase !== "grounded") return Math.max(0, Math.min(pending, max - namedCount))
  const room = Math.max(0, max - namedCount)
  return Math.max(0, Math.min(state.evidenceCount - namedCount, room))
}

/**
 * The panel badge: the reported connection count while authorizing, the
 * reported evidence count once grounded. Nothing else is counted here.
 */
export function getPrismBadge(t: TFunction, state: FilthSearchActivityState): string {
  return state.phase === "grounded"
    ? t("activity_messages.filth.evidence", {
        count: state.evidenceCount,
        defaultValue: "{{count}} evidence items",
      })
    : t("activity_messages.filth.connections", {
        count: state.backendCount,
        defaultValue: "{{count}} connections",
      })
}
